<template>
  <div class="author-statistic">
    <div>
      <p class="author-statistic__count">{{ formattedArticleCount }}</p>
      <p v-if="isFollowersCountVisible" class="author-statistic__count">
        {{ formattedFollowerCount }}
      </p>
    </div>

    <div
      v-if="isFollowAuthorButtonVisible"
      v-focusable
      :id="followAuthorIconWrapperId"
      class="author-statistic__follow-container"
      @click="onToggleFollowAuthor"
    >
      <a-icon
        :icon="ICON.WALKING_FINGERS_ROUND_BLACK"
        :width="30"
        :height="30"
        :tabindex="-1"
        :disabled="inProgress"
        is-button
        :aria-label="followAuthorButtonText"
        class="author-statistic__follow-icon"
      >
        <span slot="additional-info" class="author-statistic__follow-label">{{
          followAuthorButtonText
        }}</span>
      </a-icon>
    </div>
  </div>
</template>

<script>
import { PROP_TYPES, propValidator } from '@/utils/validators'
import mixins from '@/utils/mixins'
import AIcon, { ICON } from 'shared/AIcon'
import {
  METHOD_NAME,
  PENDING_ACTION,
  PENDING_ACTION_EVENT
} from 'enums/pending-actions'

const MIN_FOLLOWER_COUNT_TO_SHOW = 10

export default {
  name: 'AuthorStatistic',
  mixins: [
    mixins.pendingAuthActions,
    mixins.pendingAuthActionsListenersFactory({
      [PENDING_ACTION_EVENT.START_PENDING_ACTION]:
        METHOD_NAME.HANDLE_START_PENDING_ACTION,
      [PENDING_ACTION_EVENT.END_PENDING_ACTION]:
        METHOD_NAME.HANDLE_END_PENDING_ACTION
    })
  ],
  components: { AIcon },
  props: {
    articleCount: propValidator([PROP_TYPES.STRING, PROP_TYPES.NUMBER]),
    followerCount: propValidator([PROP_TYPES.STRING, PROP_TYPES.NUMBER]),
    isFollowAuthorButtonVisible: propValidator([PROP_TYPES.BOOLEAN]),
    authorId: propValidator([PROP_TYPES.STRING]),
    isFollowed: propValidator([PROP_TYPES.BOOLEAN])
  },
  data() {
    return {
      ICON,
      inProgress: false,
      guid: this.$helper.guid()
    }
  },
  computed: {
    followAuthorIconWrapperId() {
      return `${this.$options.name}-${this.guid}`
    },
    followAuthorButtonText() {
      return this.isFollowed ? 'Unfollow author' : 'Follow author'
    },
    isFollowersCountVisible() {
      return this.followerCount >= MIN_FOLLOWER_COUNT_TO_SHOW
    },
    formattedArticleCount() {
      return `${this.articleCount} ${
        this.articleCount === 1 ? 'article' : 'articles'
      }`
    },
    formattedFollowerCount() {
      return `${this.followerCount} ${
        this.followerCount === 1 ? 'follower' : 'followers'
      }`
    }
  },
  methods: {
    onToggleFollowAuthor() {
      const newFollowerCount = this.followerCount + (this.isFollowed ? -1 : 1)
      const actionData = {
        id: this.authorId,
        newValue: !this.isFollowed,
        newFollowerCount
      }

      this.$_pendingAuthActions_handleAction({
        actionType: PENDING_ACTION.FOLLOW_AUTHOR,
        actionData,
        activatorSelector: `#${this.followAuthorIconWrapperId}`
      })
    },
    [METHOD_NAME.HANDLE_START_PENDING_ACTION](refreshPayload) {
      if (refreshPayload.id === this.authorId) {
        this.inProgress = true
      }
    },
    [METHOD_NAME.HANDLE_END_PENDING_ACTION](refreshPayload) {
      if (refreshPayload.id === this.authorId) {
        this.inProgress = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.author-statistic {
  padding: 20px;
  display: flex;
  justify-content: space-between;

  @include mobile {
    padding: 0;
  }

  .author-statistic__count {
    font-size: 14px;
    line-height: 19px;
  }

  .author-statistic__follow-container {
    display: flex;
    align-items: center;
  }

  .author-statistic__follow-label {
    margin-left: 5px;
    padding-right: 3px;
    font-size: 16px;
    line-height: 22px;
    font-weight: 700;
    white-space: nowrap;
  }

  .author-statistic__follow-icon {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    width: 150px;

    &:hover {
      .author-statistic__follow-label {
        color: $c--main;
      }
    }
  }
}
</style>
